/**
 * Deemed Cancellation Config — Phase 5 (per-project settings + Legal Notice template +
 * per-day Pre-DC reminder content). Reuses reminder_master + EmailSmsTemplates
 * (project_id/wing_id columns) — see Services/DeemedCancellationConfig.js for the exact row
 * mapping. Mirrors AllotmentLetterTemplateSection. Receives projectId from ProjectDetails.
 *
 * Everything (settings + Legal Notice + every Pre-DC reminder day's OWN subject/body) is
 * edited in ONE modal with ONE Save button, opened via "Edit"/"Configure" — not an
 * always-visible inline form.
 */
import React, { Fragment, useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  getConfigForProject, saveSettings, saveNoticeTemplate, saveReminderDay, removeReminderDay, deleteConfig,
  uploadReminderAttachment,
} from "../../../Services/DeemedCancellationConfig";
import { errorTost, successTost } from "../../../Services/Toast";
import Button from "../../../Subcomponents/Button";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import cssClasses from "./ProjectDetails.module.css";

const labelSx = {
  fontFamily: "Inter-Medium",
  fontWeight: 500,
  fontSize: "1.4rem",
  lineHeight: "2rem",
  color: "#344054",
  marginBottom: "0.6rem",
  display: "block",
};

const hintSx = {
  fontFamily: "Inter-Regular",
  fontSize: "1.2rem",
  color: "#667085",
  marginTop: "0.4rem",
  whiteSpace: "pre-line",
};

const inputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#F2F4F7",
    borderRadius: "0.8rem",
    fontSize: "1.4rem",
    fontFamily: "Inter-Regular",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#EAECF0",
  },
  "& .MuiInputBase-input": {
    padding: "1rem 1.2rem",
    fontSize: "1.4rem",
    fontFamily: "Inter-Regular",
    color: "#344054",
  },
};

// Same toolbar as ProjectDetails.jsx's own Email Template tab (CKEditor) — templates configured
// here are stored as real HTML, same as that tab, instead of plain text with literal "\n".
const emailEditorConfig = {
  toolbar: [
    "paragraph", "heading1", "heading2", "heading3", "heading4", "heading5", "heading6",
    "alignment", "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList",
    "blockQuote", "undo", "redo",
  ],
};

// Same empty-value error message + red error-text style as ProjectDetails.jsx's Email Template tab.
const editorHelperTextSx = {
  fontSize: "1.4rem !important",
  margin: "0rem 0 0 0 !important",
  fontFamily: "Inter-Medium !important",
  lineHeight: "1.8rem !important",
  paddingTop: "0.5rem !important ",
  color: "#d32f2f",
};

const sectionCardSx = {
  background: "#FCFCFD",
  border: "1px solid #EAECF0",
  borderRadius: "0.8rem",
  padding: "2rem 2.4rem",
  marginBottom: "1.6rem",
};

const sectionTitleSx = {
  fontFamily: "Inter-Medium",
  fontSize: "1.5rem",
  fontWeight: 600,
  color: "#344054",
  marginBottom: "1.6rem",
};

const modalSectionTitleSx = {
  fontFamily: "Inter-Medium",
  fontSize: "1.5rem",
  fontWeight: 600,
  color: "#101828",
  marginTop: "0.8rem",
  marginBottom: "0.2rem",
};

const modalSectionHintSx = {
  fontFamily: "Inter-Regular",
  fontSize: "1.2rem",
  color: "#667085",
  marginBottom: "1.2rem",
};

const dayCardSx = {
  background: "#FFFFFF",
  border: "1px solid #EAECF0",
  borderRadius: "1rem",
  padding: "1.6rem",
  marginBottom: "1.2rem",
  position: "relative",
  transition: "border-color 0.15s ease",
};

const checkboxLabelSx = { fontFamily: "Inter-Regular", fontSize: "1.4rem", color: "#344054" };

const sectionNumberSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2.6rem",
  height: "2.6rem",
  borderRadius: "50%",
  background: "#155EEF",
  color: "#FFFFFF",
  fontFamily: "Inter-Medium",
  fontSize: "1.3rem",
  marginRight: "1rem",
  flexShrink: 0,
};

function ModalSectionHeader({ number, title, hint }) {
  return (
    <Box sx={{ marginTop: number === 1 ? 0 : "2.4rem", paddingTop: number === 1 ? 0 : "2rem", borderTop: number === 1 ? "none" : "1px solid #EAECF0" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <span style={sectionNumberSx}>{number}</span>
        <Typography sx={modalSectionTitleSx}>{title}</Typography>
      </Box>
      {hint && <Typography sx={{ ...modalSectionHintSx, marginLeft: "3.6rem" }}>{hint}</Typography>}
    </Box>
  );
}

const removeCircleSx = {
  position: "absolute",
  top: "1.2rem",
  right: "1.2rem",
  width: "2.8rem",
  height: "2.8rem",
  minWidth: "2.8rem",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#B42318",
  backgroundColor: "#FEF3F2",
  border: "1px solid #FECDCA",
  cursor: "pointer",
  fontSize: "1.6rem",
  lineHeight: 1,
  padding: 0,
};

function FormField({ label, hint, children }) {
  return (
    <Box className={cssClasses.inputWrapper}>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
      {hint && <Typography sx={hintSx}>{hint}</Typography>}
    </Box>
  );
}

// No hardcoded day defaults — a fresh project starts fully blank; every value must be
// explicitly entered and saved.
const emptyConfig = {
  triggerDay: "", releaseDay: "", enabled: true,
  legalNoticeRowId: null, legalNoticeTemplateId: null, releaseRowId: null,
};
const blankReminderRow = () => ({ id: null, day: "", templateId: null, emailSubject: "", emailTemplate: "", bodyHelperText: "", attachmentUrl: null });

export default function DeemedCancellationConfigSection({ projectId }) {
  const [config, setConfig] = useState(emptyConfig);
  const [notice, setNotice] = useState({ emailSubject: "", emailTemplate: "" });
  const [noticeBodyHelperText, setNoticeBodyHelperText] = useState("");
  const [reminderRows, setReminderRows] = useState([]); // [{id, day, templateId, emailSubject, emailTemplate}]
  // Read-only snapshot of what's actually saved in the DB right now — separate from the
  // editable state above, so the summary never shows unsaved edits still sitting in the modal.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    loadConfig();
  }, [projectId]);

  async function loadConfig() {
    try {
      const c = await getConfigForProject(projectId, null);
      setConfig({
        triggerDay: c.triggerDay || "",
        releaseDay: c.releaseDay || "",
        enabled: c.enabled,
        legalNoticeRowId: c.legalNoticeRowId,
        legalNoticeTemplateId: c.legalNoticeTemplateId,
        releaseRowId: c.releaseRowId,
      });
      setNotice({ emailSubject: c.notice?.emailSubject || "", emailTemplate: c.notice?.emailTemplate || "" });
      setNoticeBodyHelperText("");
      setReminderRows(c.reminderRows?.length ? c.reminderRows.map((r) => ({ ...r, bodyHelperText: "" })) : []);
      setSavedSnapshot({
        hasConfig: !!(c.legalNoticeRowId || c.releaseRowId),
        triggerDay: c.triggerDay || null,
        releaseDay: c.releaseDay || null,
        enabled: c.enabled,
        reminderDays: (c.reminderRows || []).map((r) => r.day),
        hasNotice: !!c.notice,
        emailSubject: c.notice?.emailSubject || "",
      });
    } catch (e) { console.error("Deemed config load failed:", e); }
  }

  const updateReminderRow = (index, field, value) => {
    setReminderRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };
  const addReminderRow = () => setReminderRows((prev) => [...prev, blankReminderRow()]);
  const removeReminderRowLocal = (index) => setReminderRows((prev) => prev.filter((_, i) => i !== index));

  // Single Save: settings -> Legal Notice -> every reminder day (each with its own content) ->
  // delete any day rows that were removed from the list. All in one action, one button.
  const onSaveAll = async () => {
    setSaving(true);
    try {
      const savedSettings = await saveSettings({
        projectId, wingId: null,
        triggerDay: Number(config.triggerDay),
        releaseDay: Number(config.releaseDay),
        enabled: config.enabled,
        legalNoticeRowId: config.legalNoticeRowId,
        legalNoticeTemplateId: config.legalNoticeTemplateId,
        releaseRowId: config.releaseRowId,
      });

      const savedNotice = await saveNoticeTemplate({
        projectId, wingId: null,
        emailSubject: notice.emailSubject, emailTemplate: notice.emailTemplate,
        legalNoticeRowId: savedSettings.legalNoticeRowId,
        legalNoticeTemplateId: savedSettings.legalNoticeTemplateId,
        triggerDay: Number(config.triggerDay),
      });

      // Delete any day that was in the original saved set but is no longer in the list.
      const originalIds = new Set((savedSettings.reminderRows || []).map((r) => r.id));
      const keptIds = new Set(reminderRows.filter((r) => r.id).map((r) => r.id));
      const idsToDelete = [...originalIds].filter((id) => !keptIds.has(id));
      for (const id of idsToDelete) {
        await removeReminderDay(id, projectId, null);
      }

      // Save every remaining day (skip fully-blank rows the user added but never filled in).
      for (const row of reminderRows) {
        if (!row.day && !row.emailSubject && !row.emailTemplate) continue;
        await saveReminderDay({
          projectId, wingId: null,
          id: row.id, templateId: row.templateId,
          day: row.day, emailSubject: row.emailSubject, emailTemplate: row.emailTemplate,
          attachmentUrl: row.attachmentUrl || null,
        });
      }

      await loadConfig();
      successTost("Deemed cancellation configuration saved.");
      setEditOpen(false);
    } catch (e) { console.error(e); errorTost(e?.message || "Failed to save configuration."); }
    finally { setSaving(false); }
  };

  // Opens the edit modal with the current saved values freshly loaded (discards any stale in-progress edits).
  const onOpenEdit = async () => {
    await loadConfig();
    setEditOpen(true);
  };

  const onDeleteConfig = async (response) => {
    if (response !== "confirmed") { setDeleteConfirmOpen(false); return; }
    setDeleting(true);
    try {
      await deleteConfig({
        projectId, wingId: null,
        legalNoticeRowId: config.legalNoticeRowId,
        releaseRowId: config.releaseRowId,
        reminderRowIds: reminderRows.map((r) => r.id).filter(Boolean),
      });
      setConfig(emptyConfig);
      setNotice({ emailSubject: "", emailTemplate: "" });
      setNoticeBodyHelperText("");
      setReminderRows([]);
      setSavedSnapshot({ hasConfig: false, triggerDay: null, releaseDay: null, enabled: true, reminderDays: [], hasNotice: false, emailSubject: "" });
      successTost("Deemed cancellation config deleted for this project.");
    } catch (e) { console.error(e); errorTost(e?.message || "Failed to delete config."); }
    finally { setDeleting(false); setDeleteConfirmOpen(false); }
  };

  return (
    <Fragment>
      <Box className={cssClasses.SubContainer}>
        <Box className={cssClasses.DivContainer}>
          <Box className={cssClasses.ProjectTitle}>Deemed Cancellation Config</Box>
        </Box>
      </Box>

      <Box height="100%" overflow="auto">
        <Box className={cssClasses.MainDiv} flexDirection="column !important">

          {/* ── Currently Saved (read-only — never shows unsaved edits) ── */}
          <Box sx={{ ...sectionCardSx, background: "#F9FAFB" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.6rem" }}>
              <Typography sx={{ ...sectionTitleSx, marginBottom: 0 }}>Currently Saved Configuration</Typography>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Button
                  style={{
                    fontSize: "1.3rem", color: "#155EEF", backgroundColor: "#EEF4FF",
                    border: "1px solid #C7D7FD", borderRadius: "0.6rem", padding: "0.6rem 1.6rem",
                  }}
                  btnName={savedSnapshot?.hasConfig ? "Edit" : "Configure"}
                  clicked={onOpenEdit}
                />
                {savedSnapshot?.hasConfig && (
                  <Button
                    style={{
                      fontSize: "1.3rem", color: "#B42318", backgroundColor: "#FEF3F2",
                      border: "1px solid #FECDCA", borderRadius: "0.6rem", padding: "0.6rem 1.6rem",
                    }}
                    btnName="Delete"
                    clicked={() => setDeleteConfirmOpen(true)}
                  />
                )}
              </Box>
            </Box>
            {!savedSnapshot ? (
              <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#667085" }}>Loading…</Typography>
            ) : !savedSnapshot.hasConfig ? (
              <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#667085" }}>
                Nothing configured yet for this project — it will be skipped by the deemed cancellation cron until you configure it.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                  <span style={{
                    fontFamily: "Inter-Medium", fontSize: "1.2rem", padding: "0.4rem 1.2rem", borderRadius: "1.6rem",
                    color: savedSnapshot.enabled ? "#027A48" : "#B42318",
                    background: savedSnapshot.enabled ? "#ECFDF3" : "#FEF3F2",
                    border: `1px solid ${savedSnapshot.enabled ? "#A6F4C5" : "#FECDCA"}`,
                  }}>
                    {savedSnapshot.enabled ? "● Enabled" : "● Disabled"}
                  </span>
                  <span style={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", padding: "0.4rem 1.2rem", borderRadius: "1.6rem", color: "#344054", background: "#F2F4F7", border: "1px solid #EAECF0" }}>
                    Trigger Day: {savedSnapshot.triggerDay ?? "—"}
                  </span>
                  <span style={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", padding: "0.4rem 1.2rem", borderRadius: "1.6rem", color: "#344054", background: "#F2F4F7", border: "1px solid #EAECF0" }}>
                    Release Day: {savedSnapshot.releaseDay ?? "—"}
                  </span>
                </Box>

                <Box>
                  <Typography sx={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", color: "#667085", marginBottom: "0.6rem" }}>
                    Pre-DC Reminder Days {savedSnapshot.reminderDays?.length ? `(${savedSnapshot.reminderDays.length})` : ""}
                  </Typography>
                  {savedSnapshot.reminderDays?.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }}>
                      {savedSnapshot.reminderDays.map((d) => (
                        <span key={d} style={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", padding: "0.3rem 1rem", borderRadius: "1.6rem", color: "#155EEF", background: "#EEF4FF", border: "1px solid #C7D7FD" }}>
                          Day {d}
                        </span>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#98A2B3" }}>None configured</Typography>
                  )}
                </Box>

                <Box sx={{ paddingTop: "1rem", borderTop: "1px solid #EAECF0" }}>
                  <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#344054" }}>
                    Legal Notice Template: <b>{savedSnapshot.hasNotice ? `"${savedSnapshot.emailSubject}"` : "Not saved yet"}</b>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Settings + Legal Notice + per-day Pre-DC content — one modal, one Save button ── */}
      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{ "& .MuiDialog-paper": { maxWidth: "120rem", width: "100%" } }}
      >
        <DialogTitle sx={{ fontFamily: "Inter-Medium", color: "#101828", fontSize: "1.8rem" }}>Deemed Cancellation Configuration</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <ModalSectionHeader number={1} title="Settings" />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!config.enabled}
                onChange={(e) => setConfig((p) => ({ ...p, enabled: e.target.checked }))}
                sx={{ "& .MuiSvgIcon-root": { fontSize: "2rem" } }}
              />
            }
            label={<Typography sx={checkboxLabelSx}>Deemed cancellation enabled for this project</Typography>}
          />
          <Box sx={{ display: "flex", gap: "1.6rem", flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 20rem" }}>
              <FormField label="DC Trigger Day" hint="Days from CRM tag date until the Legal Notice fires (window + grace, combined)">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="e.g. 37"
                  value={config.triggerDay}
                  onChange={(e) => setConfig((p) => ({ ...p, triggerDay: e.target.value }))}
                  sx={inputSx}
                />
              </FormField>
            </Box>
            <Box sx={{ flex: "1 1 20rem" }}>
              <FormField label="Release day" hint="Days from CRM tag date until the unit auto-releases">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="e.g. 45"
                  value={config.releaseDay}
                  onChange={(e) => setConfig((p) => ({ ...p, releaseDay: e.target.value }))}
                  sx={inputSx}
                />
              </FormField>
            </Box>
          </Box>

          <ModalSectionHeader number={2} title={`Legal Notice Template ${config.triggerDay ? `— fires Day ${config.triggerDay}` : ""}`} hint="Sent once, the day the unit is officially flagged for deemed cancellation." />
          <FormField label="Email Subject">
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Legal Notice: Complete your registration to avoid cancellation"
              value={notice.emailSubject || ""}
              onChange={(e) => setNotice((p) => ({ ...p, emailSubject: e.target.value }))}
              sx={inputSx}
            />
          </FormField>
          <FormField label="Email Body">
            <Box className="ck-height-apply">
              <CKEditor
                editor={ClassicEditor}
                config={emailEditorConfig}
                data={notice.emailTemplate || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setNoticeBodyHelperText(data ? "" : "Please Enter valid value.");
                  setNotice((p) => ({ ...p, emailTemplate: data }));
                }}
              />
              {noticeBodyHelperText && <Typography sx={editorHelperTextSx}>{noticeBodyHelperText}</Typography>}
            </Box>
          </FormField>
          {/* WhatsApp handled externally — no WhatsApp field here. */}

          <ModalSectionHeader number={3} title="Pre-DC Reminder Days" hint="Each day gets its own subject/body — customers on Day 15 and Day 30 can receive completely different wording." />
          {reminderRows.length === 0 && (
            <Typography sx={{ fontFamily: "Inter-Regular", fontSize: "1.3rem", color: "#98A2B3", fontStyle: "italic" }}>
              No reminder days yet — click "+ Add Reminder Day" below to create one.
            </Typography>
          )}
          {reminderRows.map((row, index) => (
            <Box key={row.id || `new-${index}`} sx={{ ...dayCardSx, borderColor: row.day ? "#D0D5DD" : "#EAECF0" }}>
              <button
                type="button"
                title="Remove this reminder day"
                style={removeCircleSx}
                onClick={() => removeReminderRowLocal(index)}
              >
                ×
              </button>

              <Typography sx={{ fontFamily: "Inter-Medium", fontSize: "1.4rem", fontWeight: 600, color: "#101828", marginBottom: "1.2rem" }}>
                {row.day ? `Day ${row.day}` : `Reminder Day #${index + 1}`}
              </Typography>

              <Box sx={{ width: "14rem", marginBottom: "0.4rem" }}>
                <FormField label="Day number">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="e.g. 15"
                    value={row.day}
                    onChange={(e) => updateReminderRow(index, "day", e.target.value)}
                    sx={inputSx}
                  />
                </FormField>
              </Box>
              <FormField label="Email Subject">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Reminder: complete your registration"
                  value={row.emailSubject}
                  onChange={(e) => updateReminderRow(index, "emailSubject", e.target.value)}
                  sx={inputSx}
                />
              </FormField>
              <FormField label="Email Body">
                <Box className="ck-height-apply">
                  <CKEditor
                    editor={ClassicEditor}
                    config={emailEditorConfig}
                    data={row.emailTemplate || ""}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      updateReminderRow(index, "bodyHelperText", data ? "" : "Please Enter valid value.");
                      updateReminderRow(index, "emailTemplate", data);
                    }}
                  />
                  {row.bodyHelperText && <Typography sx={editorHelperTextSx}>{row.bodyHelperText}</Typography>}
                </Box>
              </FormField>
              <FormField label="Attachment (optional)" hint="Not required — attach a PDF/document to this day's reminder email only if needed.">
                <Box sx={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
                  <Button
                    style={{
                      fontSize: "1.4rem", color: "#344054", backgroundColor: "#FFFFFF",
                      border: "1px solid #D0D5DD", borderRadius: "0.8rem", padding: "0.8rem 1.6rem",
                    }}
                    btnName={
                      row.attachmentUploading
                        ? "Uploading…"
                        : row.attachmentUrl
                          ? (row.attachmentUrl.split("/").pop() || "Attachment selected")
                          : "Choose file"
                    }
                    disable={row.attachmentUploading}
                    clicked={() => document.getElementById(`deemed-reminder-file-input-${index}`)?.click()}
                  />
                  <input
                    id={`deemed-reminder-file-input-${index}`}
                    hidden
                    type="file"
                    accept="application/pdf,image/*,.doc,.docx"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      if (!row.day) { errorTost("Enter the day number before attaching a file."); return; }
                      updateReminderRow(index, "attachmentUploading", true);
                      try {
                        const url = await uploadReminderAttachment(projectId, row.day, file);
                        updateReminderRow(index, "attachmentUrl", url);
                        successTost("Attachment uploaded.");
                      } catch (err) {
                        console.error(err);
                        errorTost("Failed to upload attachment.");
                      } finally {
                        updateReminderRow(index, "attachmentUploading", false);
                      }
                    }}
                  />
                  {row.attachmentUrl && !row.attachmentUploading && (
                    <>
                      <a href={row.attachmentUrl} target="_blank" rel="noreferrer" style={{ fontFamily: "Inter-Medium", fontSize: "1.3rem", color: "#155EEF" }}>
                        View
                      </a>
                      <button
                        type="button"
                        onClick={() => updateReminderRow(index, "attachmentUrl", null)}
                        style={{ fontFamily: "Inter-Medium", fontSize: "1.2rem", color: "#B42318", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </Box>
              </FormField>
            </Box>
          ))}
          <Button
            style={{
              fontSize: "1.4rem", color: "#155EEF", backgroundColor: "#F9FAFB",
              border: "1px dashed #C7D7FD", borderRadius: "0.8rem", padding: "1rem", width: "100%",
            }}
            btnName="+ Add Reminder Day"
            clicked={addReminderRow}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "1.6rem 2.4rem" }}>
          <Button
            style={{ fontSize: "1.4rem", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", borderRadius: "0.8rem", padding: "0.8rem 2rem" }}
            btnName="Cancel"
            clicked={() => !saving && setEditOpen(false)}
            disable={saving}
          />
          <Button
            style={{ fontSize: "1.4rem", color: "#FFF", backgroundColor: saving ? "#84ADFF" : "#155EEF", borderRadius: "0.8rem", padding: "0.8rem 2rem" }}
            btnName={saving ? "Saving…" : "Save"}
            clicked={onSaveAll}
            disable={saving}
          />
        </DialogActions>
      </Dialog>

      <ConfirmationAlert
        open={deleteConfirmOpen}
        close={() => !deleting && setDeleteConfirmOpen(false)}
        title="Delete deemed cancellation config for this project?"
        subTitle="This removes the trigger day, release day, enabled flag, and every Pre-DC reminder day for this project. It cannot be undone. The unit will fall back to being unconfigured (skipped by the deemed cancellation cron) until reconfigured."
        buttonTitle="Cancel"
        returnRespButton={deleting ? "Deleting…" : "Delete"}
        confirm={onDeleteConfig}
      />
    </Fragment>
  );
}
